import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AutoComplete, Button, Flex, message, Modal, Select, Tag } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import PackageContext from '@totem/components/software/software_package/packageContext';
import { TagUpdateRequest } from '@totem/components/software/software_package/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
const styles = {
  tag: {
    marginBottom: '3px',
  },
  icon: {
    marginLeft: '5px',
  },
  input: {
    width: '100%',
  },
};
const SoftwareTags = () => {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isTagsLoading, setIsTagsLoading] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] =
    useState<string>(emptyId);
  const { data, onAction, organizationId } = useContext(PackageContext);
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const canAdmin =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);
  const orgTags =
    isNotNull(data) &&
    isNotNull(data.package) &&
    isNotNull(data.package.organizationTags) &&
    data.package.organizationTags.length > 0 &&
    data.package.organizationTags[0].organizationId ===
      userProfile.organization.id
      ? data.package.organizationTags[0].tags
      : [];
  useEffect(() => {
    if (!isTagsLoading) {
      setIsTagsLoading(true);
      const uri = `${DEVICES_ENDPOINT}/software/tags`;
      fetch(uri, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: string[]) => {
          setKnownTags(result);
        })
        .then(() => {
          setIsTagsLoading(false);
        });
    }
  }, [getApiToken]);
  const handleClose = () => {
    setCurrentTag('');

    setShowEdit(false);
  };
  const sendUpdate = useCallback(
    async (request: TagUpdateRequest) => {
      if (isSending) {
        return;
      }
      fetch(`${DEVICES_ENDPOINT}/software/tags`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((response) => {
          if (response.status !== 200) {
            message.error('Tag update failed!');
          } else {
            onAction('refresh');

            handleClose();
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );
  const handleUpdateTag = (
    tagOrganizationId: string,
    action: string,
    tag: string,
  ) => {
    const request: TagUpdateRequest = {
      action,
      organizationId: tagOrganizationId,
      packageId: data.package.id,
      tag,
    };
    sendUpdate(request);
  };
  const handleSubmit = () => {
    if (currentTag !== null && currentTag !== '') {
      handleUpdateTag(selectedOrganization, 'Add', currentTag);
    }
  };
  const handleChange = (value: string) => {
    setSelectedOrganization(value);
  };
  return (
    <HeaderItem
      title={
        <Flex justify={'space-between'} align={'center'}>
          <div>Tags</div>
          <div>
            {canAdmin && (
              <IBTooltip
                content={permissions.internalSystemAdministrator.tooltip}
              >
                <IBDIcon
                  name="Add"
                  title="Add Tag"
                  onClick={() => setShowEdit(true)}
                />
              </IBTooltip>
            )}
          </div>
        </Flex>
      }
    >
      {isNotNull(data) &&
      isNotNull(data.package) &&
      isNotNull(data.package.tags)
        ? data.package.tags.map((chk) => (
            <Tag key={chk} color={'success'} style={styles.tag}>
              {chk}
              {canAdmin && (
                <IBTooltip
                  content={permissions.internalSystemAdministrator.tooltip}
                >
                  <IBDIcon
                    name="Delete"
                    title="Remove Tag"
                    style={styles.icon}
                    onClick={() => handleUpdateTag(emptyId, 'Remove', chk)}
                  />
                </IBTooltip>
              )}
            </Tag>
          ))
        : ''}
      {isNotNull(data) &&
      isNotNull(data.package) &&
      isNotNull(data.package.organizationTags)
        ? orgTags.map((chk) => (
            <Tag key={chk} color={'volcano'} style={styles.tag}>
              {chk}
              {canAdmin && (
                <IBTooltip
                  content={permissions.internalSystemAdministrator.tooltip}
                >
                  <IBDIcon
                    name="Delete"
                    title="Remove Tag"
                    style={styles.icon}
                    onClick={() =>
                      handleUpdateTag(
                        userProfile.organization.id,
                        'Remove',
                        chk,
                      )
                    }
                  />
                </IBTooltip>
              )}
            </Tag>
          ))
        : ''}
      {showEdit && (
        <Modal
          open={showEdit}
          onCancel={() => handleClose()}
          title={<ModalTitle>Add Tag</ModalTitle>}
          confirmLoading={false}
          footer={
            <ModalFooter>
              <Button onClick={() => handleClose()}>Close</Button>
              <Button onClick={handleSubmit} type="primary">
                Submit
              </Button>
            </ModalFooter>
          }
        >
          <Select
            onChange={handleChange}
            style={styles.input}
            value={selectedOrganization}
          >
            <Select.Option value={emptyId}>Global</Select.Option>
            {organizationId === userProfile.organization.id && (
              <Select.Option value={userProfile.organization.id}>
                {userProfile.organization.name}
              </Select.Option>
            )}
          </Select>
          <AutoComplete
            placeholder="Tag"
            options={
              isNotNull(knownTags)
                ? knownTags.map((chk) => {
                    return { value: chk };
                  })
                : []
            }
            value={currentTag}
            onChange={(newValue) => setCurrentTag(newValue)}
            onSelect={(newValue) => setCurrentTag(newValue)}
            style={styles.input}
          />
        </Modal>
      )}
    </HeaderItem>
  );
};
export default SoftwareTags;
